// Pluggable pipe registry for the expression evaluator's `| format:X` and `| map:X` syntax.
//
// Per task spec (PR #26 follow-up, closes skip-gap #2): `format:X` pipes are functions
// `(value, arg?) => unknown`; `map:X` pipes are lookup tables `Record<string, unknown>`.
// Callers can extend via `createPipeRegistry({format: {...}, map: {...}})`; caller-provided
// entries win over built-ins.
//
// Built-in `format` pipes:
//  - currency (existing): `(v) => "$N.NN"`
//  - date-short:          ISO 8601 `YYYY-MM-DDTHH:mm:ssZ` → `YYYY-MM-DD`
//  - short-id:            last 6 chars of string, prefixed with `…`
//  - uppercase:           String(v).toUpperCase()
//  - lowercase:           String(v).toLowerCase()
//  - truncate:            `truncate:N` — truncate to N chars, append `…` if cut
//
// `map:X` tables are entirely caller-supplied: per pilot §6 the `map:status-variant`
// table is a domain concern (e.g., `{ paid: "success", pending: "warning" }`).

/** A format pipe function — maps one value to another with an optional arg (e.g. `truncate:20`). */
export type PipeFn = (value: unknown, arg?: string) => unknown

/** Lookup table for `map:X` — string keys → any JSON-shaped value. */
export type PipeMap = Record<string, unknown>

/** The registry consumed by the expression evaluator. */
export interface PipeRegistry {
  /** Named format pipes: `format:<name>`. */
  format: Record<string, PipeFn>
  /** Named lookup tables: `map:<name>`. */
  map: Record<string, PipeMap>
}

/** User-supplied overrides merged on top of the built-ins. */
export interface CreatePipeRegistryOpts {
  format?: Record<string, PipeFn>
  map?: Record<string, PipeMap>
}

/**
 * Built-in format pipes. Caller-supplied entries override these on conflict.
 */
const builtinFormat: Record<string, PipeFn> = {
  currency(v) {
    const n = typeof v === "number" ? v : Number(v)
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(v)
  },
  "date-short"(v) {
    // Accepts ISO 8601 strings, Date instances, or epoch millis. Emits `YYYY-MM-DD`.
    let d: Date | undefined
    if (v instanceof Date) d = v
    else if (typeof v === "string") d = new Date(v)
    else if (typeof v === "number") d = new Date(v)
    if (!d || Number.isNaN(d.getTime())) return String(v ?? "")
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, "0")
    const day = String(d.getUTCDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
  },
  "short-id"(v) {
    const s = String(v ?? "")
    if (s.length <= 6) return s
    return `…${s.slice(-6)}`
  },
  uppercase(v) {
    return String(v ?? "").toUpperCase()
  },
  lowercase(v) {
    return String(v ?? "").toLowerCase()
  },
  truncate(v, arg) {
    const s = String(v ?? "")
    const n = arg === undefined ? NaN : Number(arg)
    if (!Number.isFinite(n) || n <= 0) return s
    if (s.length <= n) return s
    return `${s.slice(0, n)}…`
  },
}

/**
 * Create a pipe registry. User-provided `format` / `map` entries override the built-ins
 * on key collision; unmentioned built-ins remain available.
 */
export function createPipeRegistry(opts: CreatePipeRegistryOpts = {}): PipeRegistry {
  return {
    format: { ...builtinFormat, ...(opts.format ?? {}) },
    map: { ...(opts.map ?? {}) },
  }
}

/** Default registry — no `map:` tables. Callers supply domain-specific maps themselves. */
export function defaultPipeRegistry(): PipeRegistry {
  return createPipeRegistry()
}
